import { useQuery } from '@tanstack/react-query';
import { makeBackendApi, ITravelAgent, IHotel } from 'services/BackendApi';
import { ICompany } from 'services/BackendApi/types/CompaniesResponse';
import { ICompanyMembership } from 'services/BookingManagerApi/types/CompanyMemberships';
import { IAssignedDestination } from 'services/BackendApi/types/InternalUserResponse';

export const useTravelAgentByUuidQuery = (tauuid: string | null) => {
  return useQuery({
    queryKey: ['bookingBuilder', 'getTravelAgentByUuid', tauuid],
    queryFn: async () => {
      if (!tauuid) {
        return null;
      }
      const backendApi = makeBackendApi(tauuid);
      const result = await backendApi.getTravelAgentViaUuid(tauuid);
      return result.data.data as ITravelAgent;
    },
    enabled: !!tauuid,
  });
};

export const useCompanyByUuidQuery = (companyUuid: string | null) => {
  return useQuery({
    queryKey: ['bookingBuilder', 'getCompanyByUuid', companyUuid],
    queryFn: async () => {
      if (!companyUuid) {
        return null;
      }
      const backendApi = makeBackendApi();
      const result = await backendApi.fetchCompany(companyUuid);
      return result.data.data as ICompany;
    },
    enabled: !!companyUuid,
  });
};

export const useMembershipsByCompanyQuery = (companyUuid: string | null) => {
  return useQuery({
    queryKey: ['bookingBuilder', 'getMembershipsByCompany', companyUuid],
    queryFn: async () => {
      if (!companyUuid) {
        return [];
      }
      const backendApi = makeBackendApi();
      const result = await backendApi.fetchCompanyMemberships(companyUuid, {
        filter: {
          companyMembership: {
            companyUuid: companyUuid,
            status: true,
          },
        },
      });
      return result.data.data as ICompanyMembership[];
    },
    enabled: !!companyUuid,
  });
};

export const useCurrentUserAssignedDestinationsQuery = (userUuid: string | null, isSr: boolean) => {
  return useQuery({
    queryKey: ['currentUser', 'assignedDestinations', userUuid],
    queryFn: async () => {
      if (!userUuid) {
        return [];
      }
      const backendApi = makeBackendApi();
      const result = await backendApi.getUserAssignedDestinations(userUuid);
      const destinations: IAssignedDestination[] = result.data.data;
      return destinations.map(d => d.code);
    },
    enabled: isSr && !!userUuid,
    staleTime: Infinity,
  });
};

export const useGetHotelByUuidQuery = (hotelUuid: string | null) => {
  return useQuery({
    queryKey: ['bookingBuilder', 'getHotelByUuid', hotelUuid],
    queryFn: async () => {
      if (!hotelUuid) {
        return null;
      }
      const backendApi = makeBackendApi();
      const result = await backendApi.getHotel(hotelUuid, ['uploads']);
      // @ts-ignore legacy - getHotel returns { data: { data: IHotel } }
      return result.data.data as IHotel;
    },
    enabled: !!hotelUuid,
  });
};
